// Gallery.js
import React, { useEffect, useState } from "react";
import PhotoCard from "../components/PhotoCard";
import LoginModal from "../components/LoginModal";
import { getImagesByCollection, getCollections } from "services/StrapiService";
import { isAuthenticated, logout } from "services/authService";
import { useParams } from "../../node_modules/react-router-dom/dist/index";
import useMobileScreen from "useMobileScreen";
import { FontAwesomeIcon } from "../../node_modules/@fortawesome/react-fontawesome/index";
import { faLock, faSignOutAlt } from "../../node_modules/@fortawesome/free-solid-svg-icons/index";

const Gallery = () => {
  const [images, setImages] = useState([]);
  const [loadedCount, setLoadedCount] = useState(0);
  const [isPrivate, setIsPrivate] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);
  const [authTrigger, setAuthTrigger] = useState(0);
  const { collectionTitle } = useParams();
  const isMobile = useMobileScreen();
  const totalImages = images?.length || 0;

  function shuffleArray(array) {
    for (let i = array.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [array[i], array[j]] = [array[j], array[i]];
    }
    return array;
  }

  useEffect(() => {
    const fetchImages = async () => {
      try {
        if (collectionTitle) {
          const data = await getImagesByCollection(collectionTitle);
          if (data.data && data.data.length > 0) {
            setImages(data.data[0]?.images || []);
            setAccessDenied(false);
          } else {
            setImages([]);
            setAccessDenied(true);
          }
        }
      } catch (error) {
        if (error.response && error.response.status === 403) {
          setAccessDenied(true);
          setImages([]);
        } else {
          console.error("Failed to fetch images:", error);
        }
      }
    };

    const checkPrivacy = async () => {
      try {
        const collections = await getCollections();
        const current = collections.data?.find(
          (c) => c.title === collectionTitle
        );
        if (current?.isPrivate) {
          setIsPrivate(true);
          if (!isAuthenticated()) {
            setShowLogin(true);
            return;
          }
        }
      } catch (e) {
        console.error("Failed to check collection privacy:", e);
      }
      fetchImages();
    };

    checkPrivacy();
  }, [collectionTitle, authTrigger]);

  const handleLoginSuccess = () => {
    setShowLogin(false);
    setAuthTrigger((prev) => prev + 1);
  };

  const handleLogout = () => {
    logout();
    setImages([]);
    setAccessDenied(true);
  };

  const handleRandomize = () => {
    const shuffledImages = shuffleArray([...images]);
    setImages(shuffledImages);
  };

  const handleImageLoad = () => {
    setLoadedCount((prev) => prev + 1);
  };

  // Show login modal
  if (showLogin) {
    return (
      <LoginModal
        onSuccess={handleLoginSuccess}
        onClose={() => setShowLogin(false)}
      />
    );
  }

  // Private collection, not authenticated, no images
  if (isPrivate && accessDenied && !isAuthenticated()) {
    return (
      <div style={styles.lockedContainer}>
        <FontAwesomeIcon icon={faLock} style={styles.bigLock} />
        <h2 style={styles.lockedTitle}>This collection is private</h2>
        <button style={styles.loginButton} onClick={() => setShowLogin(true)}>
          Sign in to view
        </button>
      </div>
    );
  }

  return (
    <div style={{ position: "relative" }}>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem", paddingRight: "1rem" }}>
        {isPrivate && isAuthenticated() && (
          <button onClick={handleLogout} style={styles.logoutButton}>
            <FontAwesomeIcon icon={faSignOutAlt} /> Logout
          </button>
        )}
        <button
          onClick={(e) => {
            e.preventDefault();
            handleRandomize();
          }}
          style={{ marginTop: "-1.5rem" }}
        >
          Shuffle
        </button>
      </div>
      <div
        style={{
          display: isMobile ? "block" : "none",
          color: "gray",
          paddingTop: "2rem",
        }}
      >
        <p style={{ paddingLeft: "1rem", fontSize: "0.75rem" }}>
          Note: Use desktop device to get full potential of this installation
        </p>
      </div>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "flex-start",
          justifyContent: "space-around",
        }}
      >
        {images?.map((image) => (
          <PhotoCard
            key={image.id}
            title={image.name}
            url={`https://api.elmerikinnunen.com${image.formats.large?.url}`}
            onLoad={handleImageLoad}
          />
        ))}
        {loadedCount < totalImages && <p>Loading images...</p>}
      </div>
    </div>
  );
};

const styles = {
  lockedContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "60vh",
    color: "#888",
  },
  bigLock: {
    fontSize: "3rem",
    marginBottom: "1rem",
  },
  lockedTitle: {
    fontSize: "1.3rem",
    fontWeight: "500",
    marginBottom: "1.5rem",
  },
  loginButton: {
    padding: "0.75rem 2rem",
    borderRadius: "8px",
    border: "1px solid #333",
    backgroundColor: "#fff",
    color: "#000",
    fontSize: "1rem",
    fontWeight: "600",
    cursor: "pointer",
  },
  logoutButton: {
    marginTop: "-1.5rem",
    background: "none",
    border: "1px solid #555",
    color: "#888",
    padding: "0.3rem 0.75rem",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "0.85rem",
  },
};

export default Gallery;
